
import { Clock, UserX, AlertCircle, Eye, Sparkles, Calendar, Cake, UserPlus, Building2, Landmark, UserRound } from "lucide-react";
import Card from "../../components/Card";
import Tilt from "react-parallax-tilt";
import { useEffect, useRef, useState } from "react";
import AbsentPreviewModal from "./AbsentPreviewModal";
import { useNavigate } from "react-router-dom";
import LateEarlyModal from "./LateEarlyModal";
import UpcomingEventsModal from "./UpcomingEventsModal";
import CelebrationLoginPopup from "./CelebrationLoginPopup";

import {
  getEmployeesApi,
  adminDailyAttendanceApi,
  adminPendingLeavesApi,
  adminLeaveReportApi,
  getMyLeavesApi,
  getMyAttendanceApi,
  getMyTasksApi,
  getAssignedTasksApi,
  getFeedsApi,
  likeFeedApi,
  unlikeFeedApi,
  addCommentApi,
  sendEventWishApi,
  getOfficeHolidaysApi,
  getMyReportingApi,
} from "../../app/api";
import { toast } from "react-hot-toast";
import {
  currentMonthUpcomingOnly,
  fetchIndianPublicHolidays,
  normalizeOfficeHoliday,
} from "../../utils/publicHolidays";
import {
  getAttendanceRefreshDelayMs,
  getTodayDateInputValue,
  isBeforeAttendanceAbsentCutoff,
  getDateInputValue,
} from "../../utils/dateFormat";
import { connectSocket, getSocket } from "../../socket/socket";

const IMAGE_BASE_URL = "https://uptask.visiontrek.io";

export default function KpiStrip() {
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [upcomingAnniversaries, setUpcomingAnniversaries] = useState([]);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);
  const [showEventsModal, setShowEventsModal] = useState(false);

  const [pendingLeavesCount, setPendingLeavesCount] = useState(0);
  const [presentCount, setPresentCount] = useState(0);
  const [absentCountState, setAbsentCountState] = useState(0);
  const [showLateModal, setShowLateModal] = useState(false);

  const [absentUsers, setAbsentUsers] = useState([]);
  const [lateUsers, setLateUsers] = useState([]);
  const [earlyUsers, setEarlyUsers] = useState([]);

  const [showAbsentModal, setShowAbsentModal] = useState(false);
  const [inProgressTasksCount, setInProgressTasksCount] = useState(0);
  const [onLeaveUserIds, setOnLeaveUserIds] = useState(new Set());
  const [reportingManager, setReportingManager] = useState(null);
  const [showCelebrationPopup, setShowCelebrationPopup] = useState(false);
  const [sentCelebrationIds, setSentCelebrationIds] = useState(new Set());
  const [sendingCelebrationId, setSendingCelebrationId] = useState(null);
  const liveRefreshTimerRef = useRef(null);

  const user = JSON.parse(localStorage.getItem("user"));
  const isHR = [1, 2, 3].includes(Number(user?.role_id));
  const isPresentStatus = (status) => Number(status) === 1;
  const isActiveTaskStatus = (status = "") => {
    const normalized = String(status).trim().toUpperCase().replace(/\s+/g, "_");
    return normalized && !["DONE", "COMPLETED", "COMPLETE"].includes(normalized);
  };
  const navigate = useNavigate();

  const openAttendanceFilter = (filter) => {
    navigate("/", {
      state: {
        tab: "attendance",
        attendanceFilter: filter,
        scrollTo: "attendance-table",
      },
    });
  };

  useEffect(() => {
    loadUpcomingEvents();

    if (isHR) {
      loadAttendanceAdmin();
      loadPendingLeavesAdmin();
      loadTodayApprovedLeaves();
    } else {
      loadUserMonthlyAttendance();
      loadPendingLeavesUser();
      loadUserTasks();
      loadReportingManager();
    }
  }, []);

  useEffect(() => {
    const refreshUpcomingEvents = () => {
      if (document.visibilityState === "visible") {
        loadUpcomingEvents();
      }
    };

    window.addEventListener("uptask:employees-updated", refreshUpcomingEvents);
    window.addEventListener("focus", refreshUpcomingEvents);

    return () => {
      window.removeEventListener("uptask:employees-updated", refreshUpcomingEvents);
      window.removeEventListener("focus", refreshUpcomingEvents);
    };
  }, []);

  useEffect(() => {
    if (!isHR) return undefined;

    const refreshLeaveKpis = (event) => {
      const action = event?.detail?.action;
      if (action === "approved" || action === "rejected") {
        setPendingLeavesCount((count) => Math.max(0, count - 1));
      } else if (action === "pending") {
        setPendingLeavesCount((count) => count + 1);
      }

      loadPendingLeavesAdmin();
      loadTodayApprovedLeaves();
    };

    window.addEventListener("uptask:leave-requests-updated", refreshLeaveKpis);

    return () => {
      window.removeEventListener("uptask:leave-requests-updated", refreshLeaveKpis);
    };
  }, [isHR]);

  useEffect(() => {
    if (!isHR) return undefined;

    let timeoutId;

    const scheduleRefresh = () => {
      const delay = getAttendanceRefreshDelayMs();
      const nextDelay = delay ?? 5 * 60 * 1000;

      timeoutId = window.setTimeout(() => {
        if (delay && document.visibilityState === "visible") {
          loadAttendanceAdmin();
          loadPendingLeavesAdmin();
          loadTodayApprovedLeaves();
        }
        scheduleRefresh();
      }, nextDelay);
    };

    if (getAttendanceRefreshDelayMs() && document.visibilityState === "visible") {
        loadAttendanceAdmin();
        loadPendingLeavesAdmin();
        loadTodayApprovedLeaves();
    }

    scheduleRefresh();

    return () => window.clearTimeout(timeoutId);
  }, [isHR]);

  const loadAttendanceAdmin = async () => {
    const today = getTodayDateInputValue();
    const res = await adminDailyAttendanceApi(today, { cacheTtl: 2500 });

    if (res?.success) {
      const records = Array.isArray(res.data) ? res.data : [];
      const present = records.filter(u => isPresentStatus(u.status));
      const absent = records.filter(u => !isPresentStatus(u.status));

      let late = [];
      let early = [];

      present.forEach(u => {
        if (!u.punch_in) return;

        const [h, m] = u.punch_in.split(":").map(Number);
        const minutes = h * 60 + m;

        if (minutes > 10 * 60) late.push(u);
        if (minutes < 9 * 60 + 30) early.push(u);
      });

      setPresentCount(present.length);
      setAbsentCountState(absent.length);
      setAbsentUsers(absent);
      setLateUsers(late);
      setEarlyUsers(early);
    }
  };

  const loadTodayApprovedLeaves = async () => {
    const today = getTodayDateInputValue();
    const res = await adminLeaveReportApi({ status: 1, from_date: today, to_date: today }, { cacheTtl: 5000 });
    if (res?.success) {
      const ids = new Set(
        (res.data || [])
          .filter(r => r.approval_status === 1 &&
            r.leave_start_date <= today &&
            r.leave_end_date >= today)
          .map(r => Number(r.employee_id))
      );
      setOnLeaveUserIds(ids);
    }
  };

  useEffect(() => {
    if (!isHR) return undefined;

    const socket = getSocket() || connectSocket();
    if (!socket) return undefined;

    const refreshKpis = () => {
      window.clearTimeout(liveRefreshTimerRef.current);
      liveRefreshTimerRef.current = window.setTimeout(() => {
        loadAttendanceAdmin();
        loadTodayApprovedLeaves();
      }, 150);
    };

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refreshKpis();
    };

    const liveEvents = [
      "attendance_updated",
      "punched_in",
      "punched_out",
      "break_started",
      "break_ended",
    ];

    liveEvents.forEach((eventName) => socket.on(eventName, refreshKpis));
    window.addEventListener("focus", refreshKpis);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      liveEvents.forEach((eventName) => socket.off(eventName, refreshKpis));
      window.removeEventListener("focus", refreshKpis);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      window.clearTimeout(liveRefreshTimerRef.current);
    };
  }, [isHR]);

  const loadUserMonthlyAttendance = async () => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const res = await getMyAttendanceApi(month, year);
    if (res?.success && Array.isArray(res.data)) {
      const present = res.data.filter(r => isPresentStatus(r.status)).length;
      const absent = res.data.filter(r => !isPresentStatus(r.status)).length;
      setPresentCount(present);
      setAbsentCountState(absent);
    }
  };

  const finalAbsentCount = absentCountState;

  const loadPendingLeavesAdmin = async () => {
    const res = await adminPendingLeavesApi({ cacheTtl: 5000 });
    if (res?.success) {
      setPendingLeavesCount(res.data?.length || 0);
    }
  };

  const loadPendingLeavesUser = async () => {
    const res = await getMyLeavesApi();
    if (res?.success) {
      const pending = res.data?.leaves?.filter(l => l.approval_status === null) || [];
      setPendingLeavesCount(pending.length);
    }
  };

  const loadUserTasks = async () => {
    const [myTasksResult, teamTasksResult] = await Promise.allSettled([
      getMyTasksApi(),
      getAssignedTasksApi(),
    ]);

    const myTasksRes = myTasksResult.status === "fulfilled" ? myTasksResult.value : null;
    const teamTasksRes = teamTasksResult.status === "fulfilled" ? teamTasksResult.value : null;
    const myTasks = myTasksRes?.success && Array.isArray(myTasksRes.data) ? myTasksRes.data : [];
    const teamTasks = teamTasksRes?.success && Array.isArray(teamTasksRes.data) ? teamTasksRes.data : [];
    const currentUserId = Number(user?.id || user?.user_id);
    const reporteeTasks = teamTasks.filter((task) => Number(task.created_by) === currentUserId);

    const visibleTasks = reporteeTasks.length ? reporteeTasks : myTasks;
    setInProgressTasksCount(visibleTasks.filter((task) => isActiveTaskStatus(task.status)).length);
  };

  const loadReportingManager = async () => {
    const res = await getMyReportingApi();
    if (res?.success) {
      setReportingManager((res.data?.reportingManagers || [])[0] || null);
    }
  };

  const parseFeedMeta = (feed) => ({
    ...feed,
    meta:
      typeof feed.meta === "string" && feed.meta.startsWith("{")
        ? (() => {
          try {
            return JSON.parse(feed.meta);
          } catch {
            return {};
          }
        })()
        : feed.meta,
  });

  const getDateParts = (dateValue) => {
    if (!dateValue) return null;

    if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
      return {
        year: dateValue.getFullYear(),
        month: dateValue.getMonth(),
        day: dateValue.getDate(),
      };
    }

    const rawValue = String(dateValue).trim();
    const normalizedDate = getDateInputValue(rawValue);
    const isoMatch = normalizedDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const slashMatch = rawValue.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);

    if (isoMatch) {
      return {
        year: Number(isoMatch[1]),
        month: Number(isoMatch[2]) - 1,
        day: Number(isoMatch[3]),
      };
    }

    if (slashMatch) {
      return {
        year: Number(slashMatch[3]),
        month: Number(slashMatch[2]) - 1,
        day: Number(slashMatch[1]),
      };
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return null;

    return {
      year: parsedDate.getFullYear(),
      month: parsedDate.getMonth(),
      day: parsedDate.getDate(),
    };
  };

  const getUpcomingDate = (dateValue, today) => {
    const parts = getDateParts(dateValue);
    if (!parts) return null;

    const eventDate = new Date(
      today.getFullYear(),
      parts.month,
      parts.day
    );
    eventDate.setHours(0, 0, 0, 0);

    if (eventDate < today) {
      eventDate.setFullYear(today.getFullYear() + 1);
    }

    return eventDate;
  };

  const getFirstValue = (source, keys = []) =>
    keys.map((key) => source?.[key]).find((value) => value !== undefined && value !== null && value !== "");

  const getEmployeeName = (employee) =>
    employee.name ||
    [employee.first_name, employee.last_name].filter(Boolean).join(" ") ||
    "Team member";

  const getEmployeeImage = (employee) =>
    employee.profile_pic || employee.image || employee.avatar || employee.photo || "";

  const getImageUrl = (path = "") => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    return `${IMAGE_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
  };

  const DEMO_PERSON_NAMES = new Set([
    "moksh batra",
    "nisha mathur",
    "harsh kaur",
    "siya bansal",
  ]);

  const getPersonId = (person = {}) =>
    person.id ||
    person.user_id ||
    person.employee_id ||
    person.publisher_user ||
    person.recipient_id ||
    person.meta?.user_id ||
    person.meta?.employee_id;

  const isDemoPerson = (person = {}) => {
    const displayName = getEmployeeName(person).trim().toLowerCase();
    const email = String(person.email || person.user_email || person.work_email || person.meta?.email || "").toLowerCase();
    const marker = String(
      person.source ||
      person.origin ||
      person.type ||
      person.user_type ||
      person.employee_type ||
      person.category ||
      person.meta?.source ||
      person.meta?.user_type ||
      ""
    ).toLowerCase();

    return (
      person.is_dummy === true ||
      person.isDummy === true ||
      person.is_demo === true ||
      person.isDemo === true ||
      person.is_test === true ||
      person.isTest === true ||
      DEMO_PERSON_NAMES.has(displayName) ||
      /\b(dummy|demo|sample|test|fake)\b/.test(displayName) ||
      /\b(dummy|demo|sample|test|fake)\b/.test(email) ||
      /\b(dummy|demo|sample|test|fake)\b/.test(marker) ||
      /@(example|test|dummy|demo|sample)\./.test(email)
    );
  };

  const isInactivePerson = (person = {}) => {
    const activeValue = person.is_active ?? person.active ?? person.isActive;
    const deletedValue = person.is_deleted ?? person.deleted ?? person.isDeleted;
    const status = String(person.status || person.employee_status || person.account_status || "").toLowerCase();
    const employmentStatus = String(person.employment_status || person.work_status || "").toLowerCase();

    if (deletedValue === true || Number(deletedValue) === 1) return true;
    if (activeValue === false || Number(activeValue) === 0) return true;
    if (person.deleted_at || person.exit_date || person.last_working_day || person.relieving_date) return true;

    return ["inactive", "deleted", "ex", "ex employee", "terminated", "resigned", "left"].some(
      (value) => status === value || employmentStatus === value
    );
  };

  const isRealActivePerson = (person = {}) => !isDemoPerson(person) && !isInactivePerson(person);

  const buildRealEmployeeLookup = (employees = []) => {
    const ids = new Set();
    const names = new Set();

    employees.filter(isRealActivePerson).forEach((employee) => {
      const id = getPersonId(employee);
      const name = getEmployeeName(employee).trim().toLowerCase();
      if (id !== undefined && id !== null && id !== "") ids.add(String(id));
      if (name) names.add(name);
    });

    return { ids, names };
  };

  const isKnownRealEmployee = (person = {}, lookup = { ids: new Set(), names: new Set() }) => {
    if (!isRealActivePerson(person)) return false;
    const id = getPersonId(person);
    const name = getEmployeeName(person).trim().toLowerCase();

    if (id !== undefined && id !== null && id !== "" && lookup.ids.has(String(id))) return true;
    return Boolean(name && lookup.names.has(name));
  };

  const filterFeedEventsToRealEmployees = (events = [], employees = []) => {
    const lookup = buildRealEmployeeLookup(employees);
    if (!lookup.ids.size && !lookup.names.size) return [];

    return events
      .map((event) => {
        if (Array.isArray(event.meta?.users)) {
          const users = event.meta.users.filter((person) => isKnownRealEmployee(person, lookup));
          if (!users.length) return null;

          return {
            ...event,
            meta: {
              ...event.meta,
              users,
            },
          };
        }

        const displayName =
          event.meta?.display_name ||
          [event.meta?.first_name, event.meta?.last_name].filter(Boolean).join(" ") ||
          event.publisher_name ||
          event.title ||
          event.name;

        const eventPerson = {
          ...event,
          id: getEventRecipientId(event),
          name: displayName,
          first_name: event.meta?.first_name,
          last_name: event.meta?.last_name,
          email: event.email || event.meta?.email,
        };

        return isKnownRealEmployee(eventPerson, lookup) ? event : null;
      })
      .filter(Boolean);
  };

  const toDateKey = (date) => {
    if (!date) return "";
    const value = new Date(date);
    if (Number.isNaN(value.getTime())) return "";

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const toLocalEventDate = (date) => `${toDateKey(date)}T00:00:00`;

  const buildEmployeeEvents = (employees = []) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const windowEnd = new Date(today);
    windowEnd.setDate(today.getDate() + 30);

    const birthdayEvents = [];
    const joiningEvents = [];

    employees.filter(isRealActivePerson).forEach((employee) => {
      const displayName = getEmployeeName(employee);
      const profilePic = getEmployeeImage(employee);
      const employeeId = employee.id || employee.user_id;
      const birthdayValue = getFirstValue(employee, [
        "dob",
        "date_of_birth",
        "birth_date",
        "birthday",
      ]);
      const joinedValue = getFirstValue(employee, [
        "joined_at",
        "joining_date",
        "date_of_joining",
        "join_date",
        "created_at",
      ]);
      const birthdayDate = getUpcomingDate(birthdayValue, today);
      const joiningDate = getUpcomingDate(joinedValue, today);
      const joinedYear = getDateParts(joinedValue)?.year || today.getFullYear();

      if (birthdayDate && birthdayDate <= windowEnd) {
        birthdayEvents.push({
          id: `employee-birthday-${employee.id || employee.user_id || displayName}`,
          recipient_id: employeeId,
          user_id: employeeId,
          type: "birthday",
          event_date: toLocalEventDate(birthdayDate),
          event_date_key: toDateKey(birthdayDate),
          dob: birthdayValue,
          profile_pic: profilePic,
          readonly: true,
          meta: {
            first_name: displayName.split(" ")[0],
            last_name: displayName.split(" ").slice(1).join(" "),
            display_name: displayName,
            profile_pic: profilePic,
            user_id: employeeId,
          },
        });
      }

      if (joiningDate && joiningDate <= windowEnd) {
        const yearsCompleted = Math.max(0, today.getFullYear() - joinedYear);
        joiningEvents.push({
          id: `employee-joining-${employee.id || employee.user_id || displayName}`,
          recipient_id: employeeId,
          user_id: employeeId,
          type: yearsCompleted > 0 ? "anniversary" : "new_joiner",
          event_date: toLocalEventDate(joiningDate),
          event_date_key: toDateKey(joiningDate),
          profile_pic: profilePic,
          readonly: true,
          meta: {
            first_name: displayName.split(" ")[0],
            last_name: displayName.split(" ").slice(1).join(" "),
            display_name: displayName,
            profile_pic: profilePic,
            user_id: employeeId,
            joined_date: joinedValue,
            years_completed: yearsCompleted,
          },
        });
      }
    });

    return { birthdayEvents, joiningEvents };
  };

  const getEventRecipientId = (event) =>
    event.recipient_id ||
    event.user_id ||
    event.employee_id ||
    event.publisher_user ||
    event.meta?.user_id ||
    event.meta?.employee_id ||
    event.meta?.users?.[0]?.id ||
    event.meta?.users?.[0]?.user_id ||
    event.meta?.users?.[0]?.employee_id;

  const getEventDedupeType = (event) =>
    event.type === "anniversary_post" ? "anniversary" : (event.eventType || event.type);

  const getEventDedupeName = (event) =>
    (
      event.meta?.display_name ||
      [event.meta?.first_name, event.meta?.last_name].filter(Boolean).join(" ") ||
      [event.meta?.users?.[0]?.first_name, event.meta?.users?.[0]?.last_name].filter(Boolean).join(" ") ||
      event.publisher_name ||
      event.title ||
      event.name ||
      ""
    ).trim().toLowerCase();

  const dedupePeopleEvents = (events = []) => {
    const seen = new Set();

    return events.filter((event) => {
      if (isDemoPerson({
        ...event,
        first_name: event.meta?.first_name,
        last_name: event.meta?.last_name,
        name: event.meta?.display_name || event.title || event.name,
        email: event.email || event.meta?.email,
      })) {
        return false;
      }

      if (Array.isArray(event.meta?.users)) {
        event.meta.users = event.meta.users.filter((user) => !isDemoPerson(user));
        if (!event.meta.users.length) return false;
      }

      const eventType = getEventDedupeType(event);
      const eventDate = event.event_date_key || toDateKey(event.event_date);
      const eventName = getEventDedupeName(event);
      const recipientId = getEventRecipientId(event);
      const personKey = eventName || recipientId || event.id;
      const key = `${eventType}:${eventDate}:${personKey}`;

      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const loadUpcomingEvents = async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const windowEnd = new Date(today);
    windowEnd.setDate(today.getDate() + 30);

    const [feedsResult, employeesResult, officeHolidayResult, publicHolidayResult] = await Promise.allSettled([
      getFeedsApi({ cacheTtl: 30000 }),
      getEmployeesApi({}, { cacheTtl: 60000 }),
      getOfficeHolidaysApi(today.getFullYear(), { cacheTtl: 5 * 60 * 1000 }),
      fetchIndianPublicHolidays(today.getFullYear()),
    ]);

    const feedsRes = feedsResult.status === "fulfilled" ? feedsResult.value : null;
    const employeesRes = employeesResult.status === "fulfilled" ? employeesResult.value : null;
    const officeHolidayRes = officeHolidayResult.status === "fulfilled" ? officeHolidayResult.value : null;
    const publicHolidays = publicHolidayResult.status === "fulfilled" ? publicHolidayResult.value : [];

    const employees = employeesRes?.success ? (employeesRes.data || []) : [];
    const feedEvents = feedsRes?.success
      ? (feedsRes.data || []).map(parseFeedMeta).filter((event) => {
        if (!["birthday", "anniversary", "anniversary_post", "new_joiner"].includes(event.type)) return false;
        const eventDate = new Date(event.event_date);
        eventDate.setHours(0, 0, 0, 0);
        return eventDate >= today && eventDate <= windowEnd;
      })
      : [];
    const realFeedEvents = filterFeedEventsToRealEmployees(feedEvents, employees);

    const { birthdayEvents, joiningEvents } = employees.length
      ? buildEmployeeEvents(employees)
      : { birthdayEvents: [], joiningEvents: [] };

    const feedBirthdays = realFeedEvents.filter((event) => event.type === "birthday");
    const feedJoiningEvents = realFeedEvents.filter((event) =>
      ["anniversary", "anniversary_post", "new_joiner"].includes(event.type)
    );

    setUpcomingBirthdays(dedupePeopleEvents([...birthdayEvents, ...feedBirthdays]));
    setUpcomingAnniversaries(dedupePeopleEvents([...joiningEvents, ...feedJoiningEvents]));

    const officeHolidays = officeHolidayRes?.success
      ? (officeHolidayRes.data || []).map(normalizeOfficeHoliday)
      : [];

    const thisMonthHolidays = currentMonthUpcomingOnly([
      ...officeHolidays,
      ...(publicHolidays || []),
    ], today).sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime());

    setUpcomingHolidays(thisMonthHolidays);
  };

  const handleLike = async (feed) => {
    // Optimistic update for UI feel
    const isLiked = feed.is_liked;
    const res = isLiked ? await unlikeFeedApi(feed.id) : await likeFeedApi(feed.id);

    if (res.success) {
      loadUpcomingEvents();
    } else {
      toast.error("Failed to update like");
    }
  };

  const handleComment = async (id, comment) => {
    if (!comment.trim()) return;
    const res = await addCommentApi(id, { comment });
    if (res.success) {
      toast.success("Comment added");
      loadUpcomingEvents();
    }
  };

  const handleWish = async (event) => {
    const recipientId =
      event.recipient_id ||
      event.user_id ||
      event.employee_id ||
      event.meta?.user_id ||
      event.meta?.employee_id;

    const eventType = event.eventType || event.type;
    const displayName = event.displayName || event.meta?.display_name || event.meta?.first_name || "you";
    const senderName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.name || "Someone";
    const isBirthday = eventType === "birthday";
    const message = isBirthday
      ? `${senderName} wished you a happy birthday.`
      : `${senderName} congratulated you on your company milestone.`;

    if (!recipientId) {
      toast.error("Could not find this user's account");
      return { success: false };
    }

    const res = await sendEventWishApi({
      recipient_id: recipientId,
      event_type: eventType,
      event_date: event.event_date,
      event_date_key: event.event_date_key || toDateKey(event.event_date),
      message,
    });

    if (res?.success) {
      toast.success(`Wish sent to ${displayName}`);
    } else {
      toast.error(res?.message || "Could not send wish");
    }

    return res;
  };

  const getEventDisplayNameForPopup = (event) => {
    const firstUser = event.meta?.users?.[0] || {};
    return (
      event.displayName ||
      event.meta?.display_name ||
      [event.meta?.first_name || firstUser.first_name, event.meta?.last_name || firstUser.last_name]
        .filter(Boolean)
        .join(" ") ||
      event.title ||
      event.name ||
      "Team member"
    );
  };

  const getEventFirstLastName = (event) => {
    const firstUser = event.meta?.users?.[0] || {};
    const displayName = getEventDisplayNameForPopup(event);
    const parts = displayName.split(" ").filter(Boolean);

    return {
      firstName: event.first_name || event.meta?.first_name || firstUser.first_name || parts[0] || "Team",
      lastName: event.last_name || event.meta?.last_name || firstUser.last_name || parts.slice(1).join(" "),
    };
  };

  const getEventProfilePicForPopup = (event) => {
    const firstUser = event.meta?.users?.[0] || {};
    return (
      event.profile_pic ||
      event.profilePic ||
      event.meta?.profile_pic ||
      firstUser.profile_pic ||
      event.publisher_profile_pic ||
      ""
    );
  };

  const getEventYears = (event) =>
    Number(
      event.years ||
      event.meta?.years_completed ||
      event.meta?.users?.[0]?.years_completed ||
      0
    );

  const getCelebrationPersonKey = (event) => {
    const recipientId = getEventRecipientId(event);
    if (recipientId) return `id:${recipientId}`;

    const personName = getEventDisplayNameForPopup(event).trim().toLowerCase();
    return `name:${personName}`;
  };

  const dedupeCelebrationEvents = (events = []) => {
    const byPersonAndType = new Map();

    events.forEach((event) => {
      const eventType = getEventDedupeType(event);
      const eventDate = event.event_date_key || toDateKey(event.event_date);
      const personKey = getCelebrationPersonKey(event);
      const key = `${eventType}:${eventDate}:${personKey}`;
      const existing = byPersonAndType.get(key);
      const existingHasRecipient = Boolean(existing && getEventRecipientId(existing));
      const eventHasRecipient = Boolean(getEventRecipientId(event));

      if (!existing || (!existingHasRecipient && eventHasRecipient)) {
        byPersonAndType.set(key, event);
      }
    });

    return Array.from(byPersonAndType.values());
  };

  const getCelebrationChatMessage = (event) => {
    const personName = getEventDisplayNameForPopup(event);
    const eventType = event.eventType || event.type;
    const years = getEventYears(event);

    if (eventType === "birthday") {
      return `Happy Birthday ${personName}! Wishing you a wonderful year ahead.`;
    }

    return `Congratulations ${personName} on completing ${years || ""} year${years === 1 ? "" : "s"} with Visiontrek!`.replace(/\s+/g, " ").trim();
  };

  const openCelebrationChat = (event) => {
    const recipientId = getEventRecipientId(event);
    const { firstName, lastName } = getEventFirstLastName(event);
    const eventType = event.eventType || event.type;

    if (!recipientId) return;

    const target = {
      userId: recipientId,
      firstName,
      lastName,
      profilePic: getEventProfilePicForPopup(event),
      celebrationType: eventType,
      celebrationMessage: getCelebrationChatMessage(event),
    };

    window.__OPEN_CHAT_TARGET__ = target;
    window.__OPEN_CHAT_USER_ID__ = recipientId;
    localStorage.setItem("openChatFromNotification", JSON.stringify(target));
    window.dispatchEvent(new CustomEvent("uptask:open-chat-from-notification", { detail: target }));
  };

  const handleCelebrationCongratulations = async (event) => {
    const recipientId = getEventRecipientId(event);
    if (!recipientId) {
      toast.error("Could not find this user's account");
      return;
    }

    setSendingCelebrationId(event.popupKey);
    const res = await handleWish(event);

    if (res?.success) {
      const message = getCelebrationChatMessage(event);
      const socket = getSocket() || connectSocket();
      socket?.emit("send_message", {
        receiverId: recipientId,
        message,
        type: "text",
        clientTime: new Date(),
      });

      setSentCelebrationIds((prev) => {
        const next = new Set(prev);
        next.add(event.popupKey);
        return next;
      });
      setShowCelebrationPopup(false);
      openCelebrationChat(event);
    }

    setSendingCelebrationId(null);
  };



  const totalPeopleCount = upcomingBirthdays.length + upcomingAnniversaries.reduce((acc, curr) => acc + (curr.meta?.users?.length || 1), 0);
  const totalEventCount = totalPeopleCount + upcomingHolidays.length;

  const allUpcomingEvents = [
    ...upcomingBirthdays.map(e => ({ ...e, isBday: true })),
    ...upcomingAnniversaries.map(e => ({ ...e, isAnniv: true })),
    ...upcomingHolidays.map(e => ({
      ...e,
      isHoliday: true,
      isOfficeHoliday: e.eventType === "office_holiday",
    })),
  ].sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime());

  const closestEvent = allUpcomingEvents[0];
  const previewEvents = allUpcomingEvents.slice(0, 3);
  const todayEventKey = toDateKey(new Date());
  const todayCelebrationEvents = allUpcomingEvents
    .filter((event) => {
      const eventType = getEventDedupeType(event);
      const recipientId = getEventRecipientId(event);
      return (
        ["birthday", "anniversary"].includes(eventType) &&
        recipientId &&
        String(recipientId) !== String(user?.id) &&
        (event.event_date_key || toDateKey(event.event_date)) === todayEventKey
      );
    })
    .map((event) => {
      const eventType = getEventDedupeType(event);
      const recipientId = getEventRecipientId(event);
      const personName = getEventDisplayNameForPopup(event);
      const years = getEventYears(event);
      const birthYear = getDateParts(event.dob)?.year;
      const age = birthYear ? new Date().getFullYear() - birthYear : 0;
      const eventDateKey = event.event_date_key || toDateKey(event.event_date);
      const personKey = recipientId ? `id:${recipientId}` : `name:${personName.trim().toLowerCase()}`;

      return {
        ...event,
        eventType,
        recipient_id: recipientId,
        user_id: recipientId,
        popupKey: `${eventType}:${eventDateKey}:${personKey}`,
        displayName: personName,
        profilePic: getEventProfilePicForPopup(event),
        detail:
          eventType === "birthday"
            ? age > 0 ? `Turning ${age} today` : "Birthday celebration"
            : years > 0 ? `Completed ${years} year${years === 1 ? "" : "s"} today` : "Work anniversary milestone",
      };
    });
  const dedupedTodayCelebrationEvents = dedupeCelebrationEvents(todayCelebrationEvents);
  const celebrationPopupSignature = dedupedTodayCelebrationEvents
    .map((event) => event.popupKey)
    .sort()
    .join("|");
  const celebrationPopupStorageKey = `uptaskCelebrationPopupSeen:${user?.id || "guest"}:${todayEventKey}:${celebrationPopupSignature}`;
  const reportingManagerName = reportingManager ? getEmployeeName(reportingManager) : "";
  const reportingManagerImage = reportingManager ? getImageUrl(getEmployeeImage(reportingManager)) : "";
  const showingPunchPending = isHR && isBeforeAttendanceAbsentCutoff();
  const attendanceAlertTitle = isHR
    ? showingPunchPending
      ? "Punch Pending"
      : "Absent Today"
    : "Absent Days";
  const attendanceAlertFilter = showingPunchPending ? "waiting" : "absent";
  const gridClassName =
    !isHR && reportingManager
      ? "grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 xl:gap-5"
      : "grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 xl:gap-5";

  useEffect(() => {
    if (!celebrationPopupSignature) return;
    if (sessionStorage.getItem(celebrationPopupStorageKey) === "1") return;

    const popupTimer = window.setTimeout(() => {
      setShowCelebrationPopup(true);
      sessionStorage.setItem(celebrationPopupStorageKey, "1");
    }, 700);

    return () => window.clearTimeout(popupTimer);
  }, [celebrationPopupSignature, celebrationPopupStorageKey]);

  return (
    <>
      <CelebrationLoginPopup
        isOpen={showCelebrationPopup}
        events={dedupedTodayCelebrationEvents}
        sendingId={sendingCelebrationId}
        sentIds={sentCelebrationIds}
        onClose={() => setShowCelebrationPopup(false)}
        onSendCongratulations={handleCelebrationCongratulations}
      />

      <div className="relative z-20 mt-8 sm:mt-10 w-[calc(100%-48px)] max-w-[1520px] mx-auto px-4 sm:px-6">
        <div className={gridClassName}>
          <Tilt>
            <div onClick={() => isHR ? openAttendanceFilter("present") : navigate("/profile", { state: { tab: "attendance" } })} className="cursor-pointer hover:scale-[1.03] transition">
              <Card title={isHR ? "Present Today" : "Present Days"} value={presentCount} icon={<Clock size={16} />} bg="bg-green-100" />
            </div>
          </Tilt>

          <Tilt>
            <div onClick={() => isHR ? openAttendanceFilter(attendanceAlertFilter) : navigate("/profile", { state: { tab: "attendance" } })} className="relative group cursor-pointer hover:scale-[1.03] transition">
              <Card title={attendanceAlertTitle} value={finalAbsentCount} icon={<UserX size={16} />} bg="bg-purple-100" />
              {isHR && !showingPunchPending && absentUsers.length > 3 && (
                <button onClick={(event) => {
                  event.stopPropagation();
                  setShowAbsentModal(true);
                }}
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-white/70 p-1.5 rounded-full shadow">
                  <Eye size={14} />
                </button>
              )}
            </div>
          </Tilt>

          <Tilt>
            <div className={`relative group h-full ${isHR ? "cursor-pointer hover:scale-[1.03] transition" : ""}`} onClick={() => isHR && openAttendanceFilter("lateEarly")}>
              {isHR ? (
                <>
                  <Card
                    title="Late / Early"
                    value={`${lateUsers.length} / ${earlyUsers.length}`}
                    icon={<AlertCircle size={16} />}
                    bg="bg-red-100"
                  />
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowLateModal(true);
                    }}
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition bg-white/70 hover:bg-white p-1.5 rounded-full shadow"
                  >
                    <Eye size={14} />
                  </button>
                </>
              ) : (
                <div onClick={() => navigate("/tasks/my")} className="cursor-pointer hover:scale-[1.03] transition h-full">
                  <Card
                    title="In Progress Tasks"
                    value={inProgressTasksCount}
                    icon={<AlertCircle size={16} />}
                    bg="bg-red-100"
                  />
                </div>
              )}
            </div>
          </Tilt>

          <Tilt>
            <div onClick={() => navigate(isHR ? "/leaves/pending" : "/leaves/me")} className="cursor-pointer hover:scale-[1.03] transition">
              <Card title="Pending Leaves" value={pendingLeavesCount} icon={<Clock size={16} />} bg="bg-blue-100" />
            </div>
          </Tilt>

          {!isHR && reportingManager && (
            <Tilt>
              <div className="group relative min-h-[118px] overflow-hidden rounded-[22px] border border-white/70 bg-gradient-to-br from-cyan-50 via-white to-violet-50 p-4 shadow-[0_22px_60px_rgba(59,130,246,0.16),inset_0_1px_0_rgba(255,255,255,0.95)] transition hover:scale-[1.03] hover:shadow-[0_36px_90px_rgba(59,130,246,0.24),inset_0_1px_0_rgba(255,255,255,0.95)]">
                <div className="absolute inset-0 bg-gradient-to-br from-white/45 via-white/10 to-transparent pointer-events-none" />
                <div className="absolute -right-7 -top-7 h-20 w-20 rounded-full bg-white/40 blur-xl transition group-hover:scale-125 pointer-events-none" />
                <div className="relative flex items-start gap-3">
                  <div className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-2xl border border-white/70 bg-white/90 text-cyan-700 shadow-[0_14px_28px_rgba(15,23,42,0.14)]">
                    {reportingManagerImage ? (
                      <img
                        src={reportingManagerImage}
                        alt={`${reportingManagerName} profile`}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <UserRound size={20} strokeWidth={2.5} />
                    )}
                  </div>
                  <div className="min-w-0 pt-0.5">
                    <p className="text-[10px] font-black uppercase tracking-[0.18em] text-cyan-700/70">
                      You report to
                    </p>
                    <h2 className="mt-1 break-words text-base font-black leading-tight tracking-tight text-slate-950">
                      {reportingManagerName}
                    </h2>
                    <p className="mt-0.5 truncate text-xs font-semibold text-slate-500">
                      {reportingManager.job_title || reportingManager.email || "Reporting Manager"}
                    </p>
                  </div>
                </div>
              </div>
            </Tilt>
          )}

          <Tilt>
            <div onClick={() => setShowEventsModal(true)} className="cursor-pointer hover:scale-[1.03] transition">
              <div className={`group relative min-h-[118px] overflow-hidden rounded-[22px] border border-white/70 bg-gradient-to-br from-pink-50 via-violet-50 to-sky-50 p-4 shadow-[0_22px_60px_rgba(88,28,135,0.18),inset_0_1px_0_rgba(255,255,255,0.95)] transition hover:shadow-[0_36px_90px_rgba(88,28,135,0.28),inset_0_1px_0_rgba(255,255,255,0.95)] flex flex-col justify-between ${closestEvent?.isAnniv ? "anniversary-glow-card" : ""}`}>
                <div className="absolute inset-0 bg-gradient-to-br from-white/45 via-white/10 to-transparent pointer-events-none"></div>
                <div className="absolute -right-7 -top-7 h-20 w-20 rounded-full bg-white/40 blur-xl transition group-hover:scale-125 pointer-events-none"></div>
                <div className="absolute -inset-12 rotate-12 bg-gradient-to-r from-transparent via-white/55 to-transparent opacity-0 transition duration-700 group-hover:opacity-100 pointer-events-none"></div>

                {/* TOP LEFT ICON BOX (Professional Icon) */}
                <div className="absolute top-4 left-4 bg-white/88 p-2.5 rounded-2xl shadow-[0_14px_28px_rgba(15,23,42,0.14),inset_0_1px_0_rgba(255,255,255,0.95)] border border-white/70 flex items-center justify-center">
                  {closestEvent?.isBday ? (
                    <div className="text-pink-500 animate-pulse">
                      <Cake size={18} strokeWidth={2.5} />
                    </div>
                  ) : closestEvent?.type === "new_joiner" ? (
                    <div className="text-cyan-600 animate-pulse">
                      <UserPlus size={18} strokeWidth={2.5} />
                    </div>
                  ) : closestEvent?.isOfficeHoliday ? (
                    <div className="text-emerald-600 animate-pulse">
                      <Building2 size={18} strokeWidth={2.5} />
                    </div>
                  ) : closestEvent?.isHoliday ? (
                    <div className="text-amber-600 animate-pulse">
                      <Landmark size={18} strokeWidth={2.5} />
                    </div>
                  ) : closestEvent?.isAnniv ? (
                    <div className="text-purple-600 animate-pulse">
                      <Sparkles size={18} strokeWidth={2.5} />
                    </div>
                  ) : (
                    <div className="text-slate-500">
                      <Calendar size={18} />
                    </div>
                  )}
                </div>

                {/* COUNT BADGE */}
                {totalEventCount > 1 && (
                  <div className="absolute top-3 right-3 bg-gradient-to-tr from-purple-600 to-indigo-600 text-white text-[10px] px-2 py-0.5 rounded-full shadow-lg font-bold border border-white/20">
                    +{totalEventCount - 1}
                  </div>
                )}

                {/* CONTENT */}
                <div className="relative mt-[50px]">
                  {closestEvent ? (
                    <>
                      {(() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const eventDate = new Date(closestEvent.event_date);
                        eventDate.setHours(0, 0, 0, 0);

                        const diffTime = eventDate.getTime() - today.getTime();
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        const typeLabel = closestEvent.isBday
                          ? "Birthday"
                          : closestEvent.type === "new_joiner"
                            ? "New Joiner"
                            : closestEvent.isOfficeHoliday
                              ? "Office Holiday"
                              : closestEvent.isHoliday
                                ? "Festival"
                                : "Work Anniversary";

                        if (diffDays === 0) {
                          return <p className="text-[10px] font-bold text-purple-600/70 uppercase tracking-wider">{closestEvent.isBday ? "Today's Birthday" : `Today's ${typeLabel}`}</p>;
                        }

                        return <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mt-[-9px]">{typeLabel} in {diffDays} Day{diffDays > 1 ? 's' : ''}</p>;
                      })()}

                      <p className="text-base font-black text-gray-800 truncate max-w-[160px] ">
                        {(() => {
                          const firstName = closestEvent?.meta?.users?.[0]?.first_name || closestEvent?.meta?.display_name || closestEvent?.meta?.first_name || closestEvent?.title || "Upcoming";
                          if (!closestEvent?.isHoliday && totalPeopleCount > 1) {
                            return `${firstName} & ${totalPeopleCount - 1} others`;
                          }
                          return firstName;
                        })()}
                      </p>

                      <div className="mt-2 flex -space-x-2">
                        {previewEvents.map((event) => {
                          if (event.isHoliday) {
                            return (
                              <div
                                key={event.id}
                                title={event.title || event.name}
                                className={`grid h-7 w-7 place-items-center rounded-full border-2 border-white shadow ${
                                  event.isOfficeHoliday ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                                }`}
                              >
                                {event.isOfficeHoliday ? <Building2 size={14} /> : <Landmark size={14} />}
                              </div>
                            );
                          }

                          const firstUser = event.meta?.users?.[0] || {};
                          const profilePic =
                            event.meta?.profile_pic ||
                            firstUser.profile_pic ||
                            event.profile_pic ||
                            event.publisher_profile_pic;
                          const name =
                            event.meta?.display_name ||
                            [firstUser.first_name || event.meta?.first_name, firstUser.last_name || event.meta?.last_name]
                              .filter(Boolean)
                              .join(" ") ||
                            "Team member";

                          return (
                            <img
                              key={event.id}
                              src={profilePic ? `${IMAGE_BASE_URL}${profilePic}` : `${IMAGE_BASE_URL}/default-avatar.png`}
                              alt={name}
                              title={name}
                              className="h-7 w-7 rounded-full border-2 border-white object-cover shadow"
                            />
                          );
                        })}
                      </div>

                    </>
                  ) : (
                    <>
                      <p className="text-xs text-gray-500">Upcoming Events</p>
                      <p className="text-xs text-gray-400 mt-0.5">No upcoming events</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Tilt>
        </div>
      </div>

      {absentUsers.length > 3 && (
        <AbsentPreviewModal isOpen={showAbsentModal} onClose={() => setShowAbsentModal(false)} users={absentUsers} onLeaveUserIds={onLeaveUserIds} />
      )}
      {(lateUsers.length + earlyUsers.length) >= 0 && (
        <LateEarlyModal isOpen={showLateModal} onClose={() => setShowLateModal(false)} lateUsers={lateUsers} earlyUsers={earlyUsers} />
      )}
      <UpcomingEventsModal
        isOpen={showEventsModal}
        onClose={() => setShowEventsModal(false)}
        birthdays={upcomingBirthdays}
        anniversaries={upcomingAnniversaries}
        holidays={upcomingHolidays}
        onLike={handleLike}
        onComment={handleComment}
        onWish={handleWish}
        currentUser={user}
      />
    </>
  );
}
